import { ApiParams, queryData as fetchData, HttpClient } from 'shared-components';
import * as types from 'types';
import {
  IVisa,
  ICourse,
  OVisa,
  VisaRefusal,
  ITravelStatus,
  IVisaStatus,
  IAccommodationStatus,
  IDocuments
} from '../types';
import { AxiosResponse } from 'axios';
import { IModal } from '../store/ui/types';
import { getGlobalConfig } from '../utilities/globalConfig';
import { AppliedEUSSStatus, EUSSStatus, IMessage } from 'types';
import { getApiUrl } from '../utilities/envVariables';
import { headers } from '../utilities/helpers';

const idToken = localStorage.getItem('auth.idToken') || '';
const api = new HttpClient(getApiUrl(), { Authorization: `${idToken}`, 'Cas-Shield-Site': window.location.origin });

export const request = async <BodyType = unknown, ReturnType = unknown>(
  params: Partial<ApiParams<BodyType>>
): Promise<ReturnType | types.ErrorResponseBody> => {
  return fetchData<BodyType, ReturnType>({ ...params, domain: await getApiUrl(), uri: params.uri! });
};

export const getActiveOffers = async (
  idToken: string,
  university: string,
  studentNo: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<[IVisa, any]> => {
  return request({
    uri: `/students/${university}/${studentNo}/active-offers`,
    method: 'GET',
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};

export const setActiveOffer = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string
): Promise<any> => {
  return api.post(
    `/students/${university}/${studentNo}/active-offers`,
    {
      intakeId,
      offerId
    },
    headers(idToken)
  );
};

export const getStudentRecord = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<[IVisa, any]> => {
  return request({
    uri: `/students/${university}/${studentNo}/${intakeId}/${offerId}`,
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};
export const getStudentDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<{ documents: IDocuments }> => {
  return request({
    uri: `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs`,
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};
export const getStudentFinDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<{ financeDocuments: IDocuments }> => {
  return request({
    uri: `/students/${university}/${studentNo}/${intakeId}/${offerId}/fin-docs`,
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};
export const getStudentFollowUpDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<{ followUpDocuments: IDocuments }> => {
  return request({
    uri: `/students/${university}/${studentNo}/${intakeId}/${offerId}/follow-up-docs`,
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};
export const getStudentMessages = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string
): Promise<Partial<IVisa>> => {
  return request({
    uri: `/students/${uni}/${studentNo}/${intakeId}/${offerId}/messages`,
    idToken
  });
};

export const getInterviewQuestions = async (
  idToken: string,
  university: string,
  studentNo: string,
  interviewId: string
): Promise<any> => {
  return api.get(`students/${university}/${studentNo}/recorded-interviews/${interviewId}/questions`, headers(idToken));
};

const updateWithTimeout = async function (body: any, idToken: string, uri: string): Promise<any> {
  return request({
    uri,
    method: 'POST',
    idToken,
    body
  });
};
export interface OtherName {
  surname: string;
  givenName: string;
  reason: string;
}

export interface PIP1Update {
  givenName?: string;
  surname?: string;
  middleName?: string;
  hasOtherNames?: boolean;
  otherNames?: Array<OtherName>;
}

export interface OtherNationality {
  nationality: string;
  current: boolean;
}
export interface PIP2Update {
  gender?: string;
  dob?: string;
  birthCountry?: string;
  nationality?: string;
  appliedForEUSS?: AppliedEUSSStatus;
  grantedEUSSStatus?: EUSSStatus;
  applyingFromHomeCountry?: boolean;
  countryOfVisaApplication?: string;
  livedInUKLast12Months?: boolean;
  otherCountryReason?: string;
  hasOtherNationality?: boolean;
  otherNationalities?: Array<OtherNationality>;
}

export interface PIP3Update {
  phoneNumber?: string;
  homeAddress?: string;
  city?: string;
  postCode?: string;
  country?: string;
}

export interface PIP4Update {
  hasCriminalConvictions?: boolean;
  criminalConvictionReason?: string;
}

export interface PIP5Update {
  studentNo?: string;
  courses: Array<ICourse>;
  casReceived?: boolean;
}

export interface PIP5_1Update {
  courses: Array<ICourse>;
}

export interface PIP6Update {
  hasEnglishTest?: boolean;
  englishTestType?: string;
  englishTestTypeOther?: string;
  englishTestNotReqdReason?: string;
}

export interface PIP7Update {
  passportStatus?: string;
  passportNumber?: string;
  passportExpectedDate?: string;
  passportExpiryDate?: string;
}

export interface PIP8Update {
  withAgent: string;
}

export interface PIP9Update {
  visaSupport: boolean;
}

export interface PIP10Update {
  useWhatsapp: string;
  whatsAppPhoneNumber: string;
}

export interface StudyVisa {
  id?: string;
  visaType: string;
  level: string;
  year: string;
  isComplete?: boolean;
  incompleteReason?: string;
  isIncompleteRecorded?: boolean;
  isVisaShortened?: boolean;
  shortenedDate?: string;
}

export interface H1Update {
  lastCourse?: string;
  lastCourseOther?: string;
  lastCourseYear?: string;
  hasOverseasTravel?: boolean;
  overseasTravelTimes?: string;
  hasUkTravel?: boolean;
  ukTravelTimes?: string;
  hasPreviousUkStudy?: boolean;
  hasPreviousUkStudyVisa?: boolean;
  previousUkStudyVisas?: Array<StudyVisa>;
  lastCourseLevel?: types.CourseLevel;
  lowerOrSameLevelReason?: string;
}

export interface H2Update {
  hasOtherUkVisas?: boolean;
  otherUkVisas?: Array<OVisa>;

  hasLeaveToRemain?: boolean;
  hasBorderRefusal?: boolean;
  borderRefusalReason?: string;
  hasUkVisaRefusals?: boolean;
  ukVisaRefusals?: Array<OVisa>;
  hasOtherVisaRefusals?: boolean;
  otherVisaRefusals?: Array<VisaRefusal>;
}

export interface H3Update {
  hasDependents?: boolean;
  hasPartner?: boolean;
  partnerVisaStatus?: types.PartnerVisaStatus;
  hasChildren?: boolean;
  childrenNum?: number;
}

interface FDepositUpdate {
  courseFeeDepositReqd: string;
  courseFeeDeposit: number;
  courseFeeDepositDate: string;
  courseFeeDepositNotReqdReason: string;
  accomDepositReqd: string;
  accomDeposit: number;
  accomDepositDate: string;
}

export async function updateStudent(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body:
    | PIP1Update
    | PIP2Update
    | PIP3Update
    | PIP4Update
    | PIP5Update
    | PIP5_1Update
    | PIP6Update
    | PIP7Update
    | PIP8Update
    | PIP9Update
    | PIP10Update
    | H1Update
    | H2Update
    | H3Update
    | FDepositUpdate
    | Record<string, unknown>,
  step: string
): Promise<any> {
  return updateWithTimeout({ ...body, step }, idToken, `/students/${uni}/${studentNo}/${intakeId}/${offerId}`);
}

interface OrganisationSponsorship {
  orgType: string;
  sponsorName: string;
  isMultiCountry?: boolean;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;
}

interface ScholarshipSponsorship {
  scholarshipName: string;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;
}

interface EducationSponsorship {
  loanType: string;
  country: string;
  bankName: string;
  loanName: string;
  sponsorshipAmount: number;
  personName: string;
  loanDesc: string;
  personFirstName: string;
  personSurname: string;
  personDesc: string;
}

interface FamilySponsorship {
  familySponsorType: string;
  accountType: string;
  jointType: string;
  accountFirstName: string;
  accountSurname: string;
  otherReason: string;
  country: string;
  bankName: string;
  totalAmount: number;
  sponsorshipAmount: number;
  canWithdrawImmediately: boolean;
}

interface OtherSponsorship {
  otherDesc: string;
  otherName: string;
  sponsorshipAmount: number;
}

export async function createSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: OrganisationSponsorship | ScholarshipSponsorship | EducationSponsorship | FamilySponsorship | OtherSponsorship,
  sponsorType: string
): Promise<any> {
  return updateWithTimeout(
    { ...body, sponsorType },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/finance`
  );
}

export async function updateSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorKey: string,
  body: OrganisationSponsorship | ScholarshipSponsorship | EducationSponsorship | FamilySponsorship | OtherSponsorship,
  sponsorType: string
): Promise<any> {
  return updateWithTimeout(
    { ...body, sponsorType, sponsorKey },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/finance`
  );
}

export async function removeSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorKey: string,
  sponsorType: string
): Promise<any> {
  return updateWithTimeout(
    { sponsorType, sponsorKey, action: 'delete' },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/finance`
  );
}

export async function removeRegion(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string
): Promise<any> {
  return updateWithTimeout(
    { sponsorType, action: 'delete' },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/finance`
  );
}

// ####### Travel Status #######

export async function saveTravelStatus(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: ITravelStatus
): Promise<any> {
  return updateWithTimeout(
    body,
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/enrolment-status/travel-status`
  );
}

// ####### Visa Status #######

export async function saveVisaStatus(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: { status: IVisaStatus }
): Promise<any> {
  return updateWithTimeout(body, idToken, `/students/${uni}/${studentNo}/${intakeId}/${offerId}/visa-status`);
}

// ####### Accommodation Status #######

export async function saveAccommodationStatus(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: IAccommodationStatus
): Promise<any> {
  return updateWithTimeout(
    body,
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/enrolment-status/accommodation-status`
  );
}

// ####### offer's recordedInterview attempt #######
export interface IRecordedInterviewBody {
  interviewHash: string;
  questions?: string;
  welcomeTitle?: string;
  videoId?: string;
  attemptIndex?: number;
}
export async function saveRecordedInterviewAttempt(
  idToken: string,
  accessToken: string | null,
  uni: string,
  studentNo: string,
  body: IRecordedInterviewBody,
  pathwayProvider?: string
): Promise<any> {
  const recordedInterviewsUri = pathwayProvider
    ? `/students/pathway-provider/${pathwayProvider}/pathway/${uni}/${studentNo}/recorded-interviews`
    : `/students/${uni}/${studentNo}/recorded-interviews`;
  return api.post(recordedInterviewsUri, { ...body, accessToken }, headers(idToken));
}

export async function checkRecordedInterviewAttempt(
  idToken: string,
  accessToken: string | null,
  uni: string,
  studentNo: string,
  body: IRecordedInterviewBody,
  pathwayProvider?: string
): Promise<any> {
  const recordedInterviewsUri = pathwayProvider
    ? `/students/pathway-provider/${pathwayProvider}/pathway/${uni}/${studentNo}/recorded-interviews-check`
    : `/students/${uni}/${studentNo}/recorded-interviews-check`;
  return api.post(recordedInterviewsUri, { ...body, accessToken }, headers(idToken));
}

// ####### CAS Interview #########

export interface I1Update {
  whyUk: string;
  whyUkOther: string;
  selectedCourseReasons: Array<string>;
  selectedCourseReasonsOther: string;
  selectedUniReasons: Array<string>;
  selectedUniReasonsOther: string;
  uniChoiceReason1: string;
  uniChoiceReason2: string;
  uniChoiceReason3: string;
}

export interface I2Update {
  campus: string;
  considerOtherUni?: boolean;
  otherUni1: string;
  otherUni2: string;
  selectedResearchMethods: Array<string>;
  selectedResearchMethodsOther: string;
  whyNotOtherUni: string;
  considerOtherCourses?: boolean;
  considerOtherCourse: string;
  whyNotConsiderOtherCourse: string;
  courseAlignFutureCareer?: boolean;
  courseAlignFutureCareerOther: string;
}

export interface I3Update {
  workWhileStudy?: boolean;
  maxWorkHoursPerWeek: number;
  whyWorkWhileStudy: string;
  whyWorkWhileStudyOther: string;
  affordCourseAndLivingWithoutWorking?: boolean;
  affordCourseAndLivingWithoutWorkingNo: string;
  completeDegreeWorkCountry: string;
  expectedSalary: number;
  furtherStudyInUk?: boolean;
  furtherStudyCareerPlans: string;
}

export interface I4Update {
  liveWhileStudying: string;
  liveWhileStudyingOther: string;
  expectedWeeklyRent: number;
  commutePlan: string;
  expectedLivingExpenses: number;
  awareOfUKVIInterview?: boolean;
}

export async function updateInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: I1Update | I2Update | I3Update | I4Update,
  step: string
): Promise<any> {
  return updateWithTimeout(
    { ...body, step },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/interview`
  );
}

// ####### Visa Prep #########

export async function enableVisaPrep(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string
): Promise<any> {
  return updateWithTimeout(
    { action: 'enable' },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/visa-prep`
  );
}

export interface V1Update {
  hasAdditionalPassports: boolean;
  additionalPassports: Array<Passport>;
}

export interface Passport {
  docNumber: string;
  placeOfIssue: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
}

export interface V12Update {
  residentialAddressStartDate: string;
  homeOwnershipStatus: string;
  homeOwnershipStatusOther: string;
  hasDifferentContactDetails?: boolean;
  diffContactAddress: string;
  diffContactCity: string;
  diffContactPostcode: string;
  diffContactCountry: string;
}

export interface V21Update {
  nonHomeCountryApplicationDesc: string;
}

export interface V22Update {
  hasOtherUkVisas: boolean;
  otherUkVisas: Array<OtherUkVisas>;
}

export interface OtherUkVisas {
  visaType: string;
  issueDate: string;
  expiryDate: string;
  country: string;
  issuingAuthority: string;
}

export interface V23Update {
  tripsToUk: Array<TripsToUK>;
}

export interface TripsToUK {
  arrivalDate: string;
  departureDate: string;
  purpose: string;
}

export interface V24Update {
  hasBeenToUk: boolean;
  hasNINumber?: boolean;
  niNumber: string;
  hasReceivedMedicalTreatment?: boolean;
  hasVoluntarilyDeparted?: boolean;
  hasExclusionOrder?: boolean;
  exclusionOrders: Array<ExclusionOrders>;
}

export interface ExclusionOrders {
  exclusionDate: string;
  reason: string;
  reference: string;
}

export interface V25Update {
  leaveToRemains: Array<LeaveToRemains>;
}

export interface LeaveToRemains {
  applicationDate: string;
  applicationType: string;
  reference: string;
}

export interface V26Update {
  borderRefusals: Array<BorderRefusals>;
}

export interface BorderRefusals {
  refusalDate: string;
  airport: string;
  reason: string;
  reference: string;
}

export interface V27Update {
  hasBeenDeported: boolean;
  deportations: Array<Deportations>;
}

export interface Deportations {
  country: string;
  removalDate: string;
  airport: string;
  reason: string;
}

export interface V28Update {
  hasTravelled: boolean;
  travelItems: Array<TravelItems>;
}

export interface TravelItems {
  country: string;
  reason: string;
  reasonOther: string;
  entryDate: string;
  exitDate: string;
}

export interface V31Update {
  maritalStatus: string;
  spouseNationality: string;
  spouseSameNationality?: boolean;
  spouseFamilyName: string;
  spouseGivenName: string;
  spouseDob: string;
  spouseLivingWith?: boolean;
  spouseTravellingWith: string;
  spouseUkVisa?: boolean;
  spouseHasPassport?: boolean;
  spousePassportNumber: string;
}

export interface V32Update {
  hasFathersPersonalDetails: boolean;
  fatherNationality: string;
  fatherSameNationality?: boolean;
  fatherFamilyName: string;
  fatherGivenName: string;
  fatherDob: string;
  fatherPlaceOfBirth: string;
  fatherTravellingWith: string;
  fatherUkVisa?: boolean;
  fatherHasPassport?: boolean;
  fatherPassportNumber: string;
}

export interface V33Update {
  hasMothersPersonalDetails: boolean;
  motherNationality: string;
  motherSameNationality?: boolean;
  motherFamilyName: string;
  motherGivenName: string;
  motherDob: string;
  motherPlaceOfBirth: string;
  motherTravellingWith: string;
  motherUkVisa?: boolean;
  motherHasPassport?: boolean;
  motherPassportNumber: string;
}

export interface V34Update {
  hasGuardiansPersonalDetails: boolean;
  guardianNationality: string;
  guardianSameNationality?: boolean;
  guardianFamilyName: string;
  guardianGivenName: string;
  guardianDob: string;
  guardianPlaceOfBirth?: string;
  guardianTravellingWith: string;
  guardianUkVisa?: boolean;
  guardianHasPassport?: boolean;
  guardianPassportNumber: string;
}

export interface V35Update {
  hasDependantChildren: boolean;
  children: Array<Children>;
  childrenLivingWithYou?: boolean;
  contactAddress?: string;
  contactCity?: string;
  contactCountry?: string;
  contactPostcode?: string;
}

export interface Children {
  nationality: string;
  sameNationality?: boolean;
  familyName: string;
  givenName: string;
  dob: string;
  placeOfBirth: string;
  travellingWith: string;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
}

export interface V4Update {
  travellingWithOthers: boolean;
  otherTravellers: Array<OtherTravellers>;
}

export interface OtherTravellers {
  relationship: string;
  relationshipOther: string;
  adultOrChild: string;
  nationality: string;
  sameNationality?: boolean;
  familyName: string;
  givenName: string;
  dob: string;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
}

export interface Family {
  nationality: string;
  familyName: string;
  givenName: string;
  dob: string;
}

export interface V5Update {
  hasArrivalDate: boolean;
  arrivalDate: string;
  lengthOfStayDays: number;
  lengthOfStayMonths: number;
  lengthOfStayYears: number;
  ukPresence: boolean;
  ukFamily: boolean;
  stayingWithFamily: boolean;
  family: Array<Family>;
  familyContactAddress: string;
  familyContactCity: string;
  familyContactCountry: string;
  familyContactPostcode: string;
  familyPhoneNumber: string;
  familyEmail: string;
  hasOtherContactAddress: boolean;
  otherContactAddress: string;
  otherContactCity: string;
  otherContactCountry: string;
  otherContactPostcode: string;
  otherPhoneNumber: string;
}

export interface V6Update {
  hasCriminalCharges: boolean;
  hasTerrorismInvolvement: boolean;
  hasTerrorismConnection: boolean;
  hasTerrorismJustified: boolean;
  hasWarCrimes: boolean;
  hasBadActivities: boolean;
  language: string;
}

export interface V71Update {
  hasReceivedCas: boolean;
  studiesConfirmationNumber: string;
  courseEndDate: string;
  alreadyStartedStudying: boolean;
  studyAddress: string;
  assessedBySponsor: boolean;
}

export interface V72Update {
  qualificationAsProofOfEnglish: boolean;
  qualificationAndCountry: string;
}

interface V73Update {
  appliedThroughUCAS: boolean;
  ucasPin: string;
}

interface V8Update {
  otherFeedback: string;
  difficultiesFeedback: string;
}

export async function updateVisaPrep(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body:
    | V1Update
    | V12Update
    | V21Update
    | V22Update
    | V23Update
    | V24Update
    | V25Update
    | V26Update
    | V27Update
    | V28Update
    | V31Update
    | V32Update
    | V33Update
    | V34Update
    | V35Update
    | V4Update
    | V5Update
    | V6Update
    | V71Update
    | V72Update
    | V73Update
    | V8Update,
  step: string
): Promise<any> {
  return updateWithTimeout(
    { ...body, step },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/visa-prep`
  );
}

export const saveMessage = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  message: IMessage
): Promise<void> => {
  return updateWithTimeout(
    {
      studentID: studentNo,
      message: {
        ...message,
        isMessageError: false,
        isMessageSaving: false
      }
    },
    idToken,
    `/students/${uni}/${studentNo}/${intakeId}/${offerId}/messages`
  );
};

export const unsubscribe = async (email: string, studentID: string): Promise<void> => {
  await api.post('/unsubscribe', {
    email,
    studentID
  });
};

export const getUploadUrl = async (
  idToken: string,
  university: string,
  studentID: string,
  intakeId: string,
  offerId: string,
  uploadConf: {
    openModal: IModal;
    fileName: string;
    fileType: string;
  }
): Promise<any> => {
  return await api.post(
    `/students/${university}/${studentID}/upload`,
    {
      university,
      id: studentID,
      ...uploadConf.openModal,
      fileName: uploadConf.fileName,
      fileType: uploadConf.fileType
    },
    headers(idToken)
  );
};

export const uploadFile = async (file: any, signedRequest: string) => {
  const options = {
    method: 'PUT',
    body: file.slice()
  };

  const response = await fetch(signedRequest, options);

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response;
};

export async function updateTBTestNoDoc(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  docKey: string,
  body: any
): Promise<any> {
  return updateWithTimeout(
    {
      ...body,
      docType: 'tbTest',
      docKey: docKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs/noDoc`
  );
}

export async function updateATASApplied(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  docKey: string
): Promise<any> {
  return updateWithTimeout(
    {
      docType: 'atas',
      docKey: docKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs/applied`
  );
}

export async function updateVisaRefusalNoDoc(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string,
  docKey: string,
  body: any
): Promise<any> {
  return updateWithTimeout(
    {
      ...body,
      docType: 'visa',
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs/noDoc`
  );
}

export async function updateFinanceMetaDataForNoDoc(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> {
  if (sponsorType === 'incomeEvidence') {
    sponsorType = 'evidenceOfIncome';
  }
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/fin-docs/noDoc`
  );
}

export async function addRequiredFinanceDoc(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> {
  if (sponsorType === 'incomeEvidence') {
    sponsorType = 'evidenceOfIncome';
  }
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/fin-docs/replace`
  );
}

export async function addRequiredDoc(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> {
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs/replace`
  );
}

export const saveDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> => {
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs`
  );
};

export const saveFollowUpDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> => {
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/follow-up-docs`
  );
};

export const saveFinanceDocs = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any
): Promise<any> => {
  if (sponsorType === 'incomeEvidence') {
    sponsorType = 'evidenceOfIncome';
  }
  return updateWithTimeout(
    {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/fin-docs`
  );
};

export const toggleDocStatus = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string,
  status: string,
  infoOnlyRequirementsOption: string,
  metadata: any
): Promise<any> => {
  return updateWithTimeout(
    {
      status,
      infoOnlyRequirementsOption,
      ...metadata,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/docs/update-status`
  );
};

export const toggleFinDocStatus = async (
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string,
  status: string,
  infoOnlyRequirementsOption: string,
  metadata: any
): Promise<any> => {
  if (sponsorType === 'incomeEvidence') {
    sponsorType = 'evidenceOfIncome';
  }
  return updateWithTimeout(
    {
      status,
      infoOnlyRequirementsOption,
      ...metadata,
      docType,
      docKey,
      sponsorType,
      sponsorKey
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/fin-docs/update-status`
  );
};

export async function markMessagesRead(
  idToken: string,
  university: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  messageIds: Array<string>
): Promise<any> {
  return updateWithTimeout(
    {
      messageIds
    },
    idToken,
    `/students/${university}/${studentNo}/${intakeId}/${offerId}/messages/markRead`
  );
}

export const getConfig = async (idToken: string, university: string): Promise<any> => {
  const uniCode = (await getGlobalConfig()).uni;
  return await api.get(`/config/${uniCode}`, headers(idToken));
};

export const getNavigationUrls = async (
  idToken: string,
  university: string
): Promise<{ apply?: string; casStudent?: string; casStaff?: string } | types.ErrorResponseBody> => {
  return await request({
    uri: `/config/navigation-urls/${university}`,
    idToken,
    retry: false
  });
};

export const getUniUser = async (idToken: string, university: string, username: string): Promise<any> => {
  return await api.get(`/users/staff/${university}/${encodeURIComponent(username)}`, headers(idToken));
};

export const exceededStudentRecordedInterview = async (
  idToken: string,
  uni: string,
  interviewHash: string,
  studentNumber: string,
  intakeId: string,
  offerId: string,
  interviewId: string,
  pathway?: string
): Promise<AxiosResponse<unknown, any>> => {
  try {
    return await api.post(
      `/students/${uni}/${studentNumber}/${intakeId}/${offerId}/recorded-interview/exceeded`,
      { interviewHash, uni, interviewId, pathway },
      headers(idToken)
    );
  } catch (e) {
    console.error(JSON.stringify(e));
    throw e;
  }
};

export const getStudentRecordedInterviews = async (
  idToken: any,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  onRetry: (retryNumber: number, time: number) => void,
  onReconnected: () => void,
  onFailed: (e: Error) => void
): Promise<Partial<IVisa>> => {
  return request({
    uri: `/students/${uni}/${studentNo}/${intakeId}/${offerId}/recorded-interview`,
    idToken,
    retry: true,
    onRetry,
    onReconnected,
    onFailed
  });
};
